import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { toast } from 'react-toastify';
import { Get, Post } from '@app/utils/httpProvider';
import { ASSET_API, ASSETGROUP_API } from '@app/utils/apiUrl';

const QRScanner = () => {
    const history = useHistory();
    const [scanner, setScanner] = useState(null);
    const [scannedCode, setScannedCode] = useState('');
    const [unregisteredMode, setUnregisteredMode] = useState(false);
    const [assets, setAssets] = useState([]);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);
    const [isLoadingAssets, setIsLoadingAssets] = useState(false);
    const [assetGroups, setAssetGroups] = useState([]);
    const [selectedAssetGroupId, setSelectedAssetGroupId] = useState('');
    const scannerRef = useRef(null);

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const res = await Get(ASSETGROUP_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 });
                if (res?.data) {
                    setAssetGroups(res.data);
                }
            } catch (err) {
                console.error("Failed to fetch asset groups", err);
            }
        };
        fetchGroups();
    }, []);

    useEffect(
        () => {
        // If in unregisteredMode, do not start camera
        if (unregisteredMode) return;

        const scannerConfig = {
            fps: 10,
            qrbox: (width, height) => {
                const minDimension = Math.min(width, height);
                const qrboxSize = Math.floor(minDimension * 0.65);
                return { width: qrboxSize, height: qrboxSize };
            },
            aspectRatio: 1.0
        };

        const onScanSuccess = async (decodedText) => {
            console.log(`Scan Result: ${decodedText}`);
            if (!decodedText) return;

            // Stop camera immediately to prevent multiple scans
            if (scannerRef.current) {
                scannerRef.current.clear().catch(err => console.error("Failed to clear scanner", err));
            }

            setScannedCode(decodedText);
            toast.dismiss();
            toast.info("Đang kiểm tra mã QR...");

            try {
                // Call API to look up asset by QR code
                const asset = await Get(ASSET_API.GET_BY_QR, null, { qrCode: decodedText });
                
                toast.dismiss();
                if (asset && asset.id) {
                    toast.success("Tìm thấy tài sản!");
                    history.push(`/asset/view/${asset.id}`);
                } else {
                    toast.warn("Mã QR chưa được khai báo trên hệ thống.");
                    setUnregisteredMode(true);
                }
            } catch (error) {
                console.error("Lookup QR code failed", error);
                toast.dismiss();
                toast.error("Lỗi khi kết nối hệ thống để kiểm tra mã QR.");
                // Allow re-scanning by reloading
                window.location.reload();
            }
        };

        const onScanFailure = (error) => {
            // Scanner failures occur continuously during camera feed, no need to log
        };

        const newScanner = new Html5QrcodeScanner("reader", scannerConfig, false);
        newScanner.render(onScanSuccess, onScanFailure);
        setScanner(newScanner);
        scannerRef.current = newScanner;

        return () => {
            if (newScanner) {
                newScanner.clear().catch(err => console.error("Failed to clear scanner on unmount", err));
            }
        };
    }, [history, unregisteredMode]);

    const handleSearchAssets = async () => {
        setIsLoadingAssets(true);
        try {
            const params = {
                Keyword: searchTerm,
                PageIndex: 1,
                PageSize: 50
            };
            if (selectedAssetGroupId) {
                params.AssetGroupId = selectedAssetGroupId;
            }
            const res = await Get(ASSET_API.GET_PAGING, null, params);
            if (res && res.data) {
                // Chỉ lấy các tài sản chưa có mã QR (qrCode rỗng hoặc null)
                const unassignedAssets = res.data.filter(asset => !asset.qrCode);
                setAssets(unassignedAssets);
            } else {
                setAssets([]);
            }
        } catch (err) {
            console.error(err);
            toast.error("Không thể tìm kiếm danh sách tài sản.");
        } finally {
            setIsLoadingAssets(false);
        }
    };

    useEffect(() => {
        if (unregisteredMode) {
            handleSearchAssets();
        }
    }, [unregisteredMode, selectedAssetGroupId]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearchAssets();
        }
    };

    const handleAssignQrCode = async () => {
        if (!selectedAsset) {
            toast.warn("Vui lòng chọn một tài sản.");
            return;
        }

        setIsAssigning(true);
        try {
            const payload = {
                assetId: selectedAsset.id,
                qrCode: scannedCode
            };
            const response = await Post(ASSET_API.ASSIGN_QR, null, payload);
            
            if (response.ok || response.status === 200) {
                toast.success("Gán mã QR Code thành công!");
                history.push(`/asset/view/${selectedAsset.id}`);
            } else {
                toast.error("Gán mã QR thất bại. Vui lòng thử lại.");
            }
        } catch (err) {
            console.error(err);
            const errMsg = err?.message || "Đã xảy ra lỗi khi gán mã QR.";
            toast.error(errMsg);
        } finally {
            setIsAssigning(false);
        }
    };

    if (unregisteredMode) {
        return (
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 text-dark">Gán mã QR</h1>
                        </div>
                    </div>
                </div>

                <section className="content mt-3">
                    <div className="container-fluid">
                        <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                            <div className="bg-warning p-3 text-white text-center">
                                <i className="fas fa-link fa-2x mb-1"></i>
                                <h5 className="mb-0 font-weight-bold">QR chưa được liên kết tài sản</h5>
                            </div>
                            
                            <div className="card-body p-4">
                                <div className="text-center mb-4">
                                    <div className="alert alert-secondary border-0 d-inline-block px-4 py-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                        <small className="text-muted d-block text-uppercase font-weight-bold">Nội dung mã QR vừa quét:</small>
                                        <code className="text-dark font-weight-bold" style={{ fontSize: '15px' }}>{scannedCode}</code>
                                    </div>
                                    <p className="text-muted mt-2">Mã QR này chưa được gán cho bất kỳ tài sản nào. Vui lòng chọn một tài sản dưới đây để liên kết.</p>
                                </div>

                                <div className="row mb-4">
                                    <div className="col-md-4 mb-3 mb-md-0">
                                        <label className="font-weight-bold text-secondary">Nhóm tài sản</label>
                                        <select 
                                            className="form-control shadow-sm" 
                                            value={selectedAssetGroupId}
                                            onChange={(e) => setSelectedAssetGroupId(e.target.value)}
                                            style={{ borderRadius: '8px', height: '45px', border: '1px solid #ced4da' }}
                                        >
                                            <option value="">-- Tất cả nhóm tài sản --</option>
                                            {assetGroups.map(group => (
                                                <option key={group.id} value={group.id}>{group.assetGroupName}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-8">
                                        <label className="font-weight-bold text-secondary">Tìm kiếm tài sản</label>
                                        <div className="input-group shadow-sm" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                                            <input 
                                                type="text" 
                                                className="form-control border-right-0" 
                                                placeholder="Nhập mã, tên tài sản, serial hoặc phòng ban..." 
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                onKeyDown={handleKeyDown}
                                                style={{ height: '45px' }}
                                            />
                                            <div className="input-group-append">
                                                <button className="btn btn-primary px-4 qr-search-btn" type="button" onClick={handleSearchAssets}>
                                                    <i className="fas fa-search qr-search-icon"></i>
                                                    <span className="qr-search-text">Tìm kiếm</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="asset-list-container overflow-auto mb-4" style={{ maxHeight: '380px', padding: '6px' }}>
                                    {
                                    isLoadingAssets ? 
                                    (
                                        <div className="p-5 text-center text-muted border rounded" style={{ backgroundColor: '#fdfdfd' }}>
                                            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '1.5rem', height: '1.5rem' }}></div>
                                            <p className="mb-0">Đang tải danh sách tài sản...</p>
                                        </div>
                                    ) : 
                                    assets.length === 0 ? (
                                        <div className="p-5 text-center text-muted border rounded" style={{ backgroundColor: '#fdfdfd' }}>
                                            <i className="fas fa-box-open fa-3x mb-3 opacity-25"></i>
                                            <p className="mb-0">{searchTerm ? 'Không tìm thấy tài sản nào phù hợp.' : 'Không có tài sản khả dụng trên hệ thống.'}</p>
                                        </div>
                                    ) : (
                                        <div className="qr-card-grid">
                                            {assets.map((asset) => {
                                                const hasQr = !!asset.qrCode;
                                                const isSelected = selectedAsset?.id === asset.id;
                                                return (
                                                    <div 
                                                        key={asset.id} 
                                                        className={`qr-asset-card ${isSelected ? 'selected' : ''} ${hasQr ? 'disabled' : ''}`}
                                                        onClick={() => {
                                                            if (!hasQr) {
                                                                setSelectedAsset(asset);
                                                            }
                                                        }}
                                                    >
                                                        <div className="qr-card-header">
                                                            <span className="qr-card-title">{asset.assetName}</span>
                                                            <div className="custom-control custom-radio">
                                                                <input 
                                                                    type="radio" 
                                                                    className="custom-control-input"
                                                                    checked={isSelected}
                                                                    disabled={hasQr}
                                                                    readOnly
                                                                />
                                                                <span className="custom-control-label"></span>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="qr-card-body">
                                                            <div className="qr-info-row">
                                                                <span className="qr-info-label">Mã tài sản:</span>
                                                                <span className="qr-info-value font-weight-bold">{asset.assetCode}</span>
                                                            </div>
                                                            {asset.serialNumber && (
                                                                <div className="qr-info-row">
                                                                    <span className="qr-info-label">Số Serial:</span>
                                                                    <span className="qr-info-value">{asset.serialNumber}</span>
                                                                </div>
                                                            )}
                                                            {asset.departmentName && (
                                                                <div className="qr-info-row">
                                                                    <span className="qr-info-label">Phòng ban:</span>
                                                                    <span className="qr-info-value">{asset.departmentName}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        <div className="qr-card-footer">
                                                            {hasQr ? (
                                                                <span className="badge badge-secondary px-2 py-1">
                                                                    <i className="fas fa-qrcode mr-1"></i> Đã gán QR
                                                                </span>
                                                            ) : (
                                                                <span className="badge badge-success-light px-2 py-1">
                                                                    Chưa có QR
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className="d-flex justify-content-between pt-3 border-top qr-action-buttons">
                                    <button 
                                        className="btn btn-outline-secondary px-4 rounded-pill"
                                        onClick={() => {
                                            setUnregisteredMode(false);
                                            setScannedCode('');
                                            setSelectedAsset(null);
                                            setAssets([]);
                                        }}
                                    >
                                        <i className="fas fa-chevron-left mr-2"></i> Quay lại quét
                                    </button>
                                    
                                    <button 
                                        className="btn btn-primary px-5 rounded-pill shadow"
                                        disabled={!selectedAsset || isAssigning}
                                        onClick={handleAssignQrCode}
                                        style={{ fontWeight: '600' }}
                                    >
                                        {isAssigning ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                Đang liên kết...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-check-circle mr-2"></i> Xác nhận gán mã
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                
                <style dangerouslySetInnerHTML={{ __html: `
                    .bg-primary-light {
                        background-color: #f1f7ff !important;
                    }
                    .border-left-primary {
                        border-left: 4px solid #007bff !important;
                    }
                    .active-item .text-dark {
                        color: #0056b3 !important;
                    }
                    .active-item .text-muted {
                        color: #6688b3 !important;
                    }
                    .badge-success-light {
                        background-color: #e2fbe8;
                        color: #1e7e34;
                        border: 1px solid #c3e6cb;
                    }
                    .qr-search-icon {
                        margin-right: 8px;
                    }
                    .qr-card-grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 16px;
                    }
                    .qr-asset-card {
                        background: #ffffff;
                        border: 1px solid #e0e0e0;
                        border-radius: 10px;
                        padding: 16px;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                        transition: all 0.25s ease;
                        cursor: pointer;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.02);
                        position: relative;
                    }
                    .qr-asset-card:hover {
                        border-color: #007bff;
                        box-shadow: 0 4px 12px rgba(0, 123, 255, 0.08);
                        transform: translateY(-2px);
                    }
                    .qr-asset-card.selected {
                        border-color: #007bff;
                        background-color: #f1f7ff;
                        box-shadow: 0 4px 12px rgba(0, 123, 255, 0.12);
                    }
                    .qr-asset-card.disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                        background-color: #fcfcfc;
                        border-color: #e0e0e0;
                    }
                    .qr-asset-card.disabled:hover {
                        transform: none;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.02);
                        border-color: #e0e0e0;
                    }
                    .qr-card-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        margin-bottom: 12px;
                    }
                    .qr-card-title {
                        font-weight: 700;
                        font-size: 15px;
                        color: #2c3e50;
                        line-height: 1.3;
                        padding-right: 24px;
                    }
                    .qr-card-body {
                        font-size: 13px;
                        color: #606f7b;
                        margin-bottom: 12px;
                        flex-grow: 1;
                    }
                    .qr-info-row {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 4px;
                    }
                    .qr-info-label {
                        color: #8795a1;
                    }
                    .qr-info-value {
                        color: #3d4852;
                        text-align: right;
                        padding-left: 8px;
                    }
                    .qr-card-footer {
                        display: flex;
                        justify-content: flex-start;
                        align-items: center;
                    }
                    @media (max-width: 767.98px) {
                        .qr-card-grid {
                            grid-template-columns: 1fr;
                            gap: 12px;
                        }
                    }
                    @media (max-width: 575.98px) {
                        .qr-search-text {
                            display: none !important;
                        }
                        .qr-search-icon {
                            margin-right: 0 !important;
                        }
                        .qr-search-btn {
                            padding-left: 16px !important;
                            padding-right: 16px !important;
                            display: flex !important;
                            align-items: center !important;
                            justify-content: center !important;
                        }
                        .qr-action-buttons {
                            flex-direction: column-reverse !important;
                            gap: 12px !important;
                        }
                        .qr-action-buttons .btn {
                            width: 100% !important;
                            margin: 0 !important;
                            padding-top: 12px !important;
                            padding-bottom: 12px !important;
                        }
                    }
                `}} />
            </div>
        );
    }

    return (
        <div className="content-header">
            <div className="container-fluid">
                <div className="row mb-2">
                    <div className="col-sm-6">
                        <h1 className="m-0 text-dark">Quét mã QR Tài sản</h1>
                    </div>
                </div>
            </div>

            <section className="content mt-2">
                <div className="container-fluid">
                    <div className="card shadow-sm border-0 overflow-hidden" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-0">
                            <div className="bg-primary p-3 text-white text-center">
                                <i className="fas fa-qrcode fa-3x mb-2"></i>
                                <p className="mb-0 font-weight-bold">Di chuyển camera đến vùng chứa mã QR</p>
                                <small className="opacity-75">Hệ thống sẽ tự động kiểm tra và mở thông tin tài sản</small>
                            </div>
                            
                            <div className="p-4 d-flex justify-content-center bg-light">
                                <div id="reader" style={{ width: '100%', maxWidth: '500px', border: 'none', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}></div>
                            </div>

                            <div className="p-3 text-center text-muted border-top bg-white">
                                <button 
                                    className="btn btn-outline-secondary btn-sm rounded-pill px-4"
                                    onClick={() => history.goBack()}
                                >
                                    <i className="fas fa-arrow-left mr-2"></i> Quay lại
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <style dangerouslySetInnerHTML={{ __html: `
                #reader__scan_region {
                    background: #fff;
                }
                #reader__dashboard_section_csr button {
                    background-color: #007bff !important;
                    color: white !important;
                    border: none !important;
                    padding: 8px 16px !important;
                    border-radius: 4px !important;
                    cursor: pointer !important;
                    margin: 10px 0 !important;
                }
                #reader img {
                    display: none;
                }
                #reader__status_span {
                    display: none !important;
                }
            `}} />
        </div>
    );
};

export default QRScanner;
